import EventEmitter from "eventemitter3";
import { WritableDraft } from "immer";
import { Id } from "PFTypes";
import {
  FilterKeyV2,
  FiltersMetaV2,
  FiltersV2,
  FilterV2,
  FilterV2WithValueOnly,
  FilterValueV2
} from "PFTypes/filters_v2";
import { PageTarget } from "PFTypes/saved_filters";
import { RequireAtLeastOne } from "type-fest";
import { StoreApi } from "zustand";

import { FiltersV2EventHandlers } from "./filters_v2_events";

export enum FiltersV2StorePage {
  BookingModule = "booking_module",
  BookingModuleApprovals = "booking_module_approvals",
  AdminBookingProfileCalendars = "admin_booking_profile_calendars",
  AdminBookingCalendarAssignees = "admin_booking_calendars_assignees",
  AdminCustomFields = "admin_custom_fields",
  AdminUsers = "admin_users",
  AdminKPIConfiguration = "admin_kpi_configuration",
  Analytics = "analytics",
  MatchesSidePanel = "matches_side_panel",
  SuggestedWMSidePanelMatches = "suggested_wm_side_panel_matches",
  SuggestedWMSidePanelShortlists = "suggested_wm_side_panel_shortlists",
  ActivityShortlistPage = "activity_shortlist_page",
  Workflow = "workflow",
  MarketplaceWorkOpportunities = "marketplace_work_opportunities"
}

export type FiltersV2Data<T extends FilterV2 | FilterV2WithValueOnly = FilterV2> = {
  mainFilters: FiltersV2<T>;
  childrenFilters: FiltersV2<T>;
  areChildrenOn: boolean;
};

export type FiltersV2State = FiltersV2Data & {
  id: FiltersV2StorePage | string;
  collection?: PageTarget;
  searchId?: Id;
  scope?: number | null; // number of entries in current collection search results
  buffer: FiltersV2Data;
  initialFilters?: FiltersMetaV2<FilterV2WithValueOnly>;
  isSidePanelOpen: boolean;
  isAutoRefreshOn: boolean;
  areLoading: boolean;
  persistent: boolean;
  filtersExcludedByScope: (FilterV2WithValueOnly & { limit: number })[];
  eventsBus: EventEmitter<FiltersV2EventHandlers>;
};

export type FilterV2Defaults = Partial<FiltersMetaV2<FilterV2WithValueOnly>>;

export type FilterV2Identifier = RequireAtLeastOne<
  Pick<FilterV2, "originalPath" | "name">,
  "originalPath" | "name"
>;

export type FilterV2WithIdentifierAndUpdater = Partial<Omit<FilterV2, "originalPath" | "name" | "value">> &
  FilterV2Identifier & {
    value?: FilterValueV2 | ((currentValue: FilterValueV2) => FilterValueV2);
  };

export type ClearValuesOptions = { subset?: FilterKeyV2[] };
export type ResetFilterValuesOptions = {
  defaults?: FilterV2Defaults;
  subset?: FilterKeyV2[];
  ignoreFilterDefaultValues?: boolean;
  removeChildrenValues?: boolean;
};
export type FiltersV2StoreType = FiltersV2State & {
  actions: {
    // actions to sync filters state with meta
    setFilters: (filtersMeta: FiltersMetaV2) => void;
    resetFilters: (initialFilters?: FiltersMetaV2<FilterV2WithValueOnly>) => void;
    setSearchId: (id?: Id) => void;
    setScope: (entriesAmount?: number) => void;
    setFiltersCollection: (collection?: PageTarget) => void;

    // filters modifications triggered by user
    updateFilter: (filter: FilterV2WithIdentifierAndUpdater) => void;
    updateFilters: (filters: FilterV2WithIdentifierAndUpdater[]) => void;
    updateBufferedFilter: (filter: FilterV2WithIdentifierAndUpdater) => void;
    restoreBufferedFilters: () => void;
    applyBufferedFilters: () => void;
    clearValues: (options?: ClearValuesOptions) => void;
    clearBufferValues: (options?: ClearValuesOptions) => void;
    resetValues: (options?: ResetFilterValuesOptions) => void;
    resetBufferValues: (options?: ResetFilterValuesOptions) => void;
    toggleChildrenFilters: (on?: boolean) => void;

    // filters components state which can be manipulated by the user
    toggleChildrenFiltersInBuffer: (on?: boolean) => void;
    toggleAutoRefresh: (on?: boolean) => void;
    toggleAreLoading: (on?: boolean) => void;
    toggleFiltersSidePanel: (open?: boolean) => void;
  };
};

export type FiltersV2ContextType = StoreApi<FiltersV2StoreType>;

export type FiltersV2StoreSetter = (
  nextStateOrUpdater:
    | FiltersV2StoreType
    | Partial<FiltersV2StoreType>
    | ((state: WritableDraft<FiltersV2StoreType>) => void),
  shouldReplace?: false
) => void;
